import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, CheckCircle, Download, ShoppingCart, Send, Users, BarChart3, FileSpreadsheet } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Section } from '../components/Section';
export function Home() {
  const heroRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });
  const yBg = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  return <div className="overflow-hidden" data-id="element-178">
      {/* Hero Section */}
      <div ref={heroRef} className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-to-br from-[#EEF2FF] via-white to-[#F5F3FF]" data-id="element-179">
        <motion.div style={{
        y: yBg
      }} className="absolute inset-0 z-0 opacity-30" data-id="element-180">
          <div className="absolute top-20 right-0 w-96 h-96 bg-[#6366F1] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" data-id="element-181"></div>
          <div className="absolute top-40 left-20 w-72 h-72 bg-[#EC4899] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" data-id="element-182"></div>
        </motion.div>

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-183">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-184">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} data-id="element-185">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6366F1]/10 text-[#6366F1] text-sm font-medium mb-6" data-id="element-186">
                <span className="flex h-2 w-2 rounded-full bg-[#6366F1]" data-id="element-187"></span>
                Chrome Extension Available Now
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6" data-id="element-188">
                Capture Any Cart. <br data-id="element-189" />
                <span className="text-[#6366F1]" data-id="element-190">
                  Send to Finance Instantly.
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-lg" data-id="element-191">
                Shop anywhere online, capture your cart with one click, and send
                purchase details to your procurement system or download as
                Excel.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-192">
                <Button variant="primary" className="text-lg px-8" data-id="element-193">
                  Install Free Extension
                </Button>
                <Button variant="outline" className="text-lg px-8" onClick={() => window.open('https://calendly.com/alex-thruline/cart-tracker', '_blank')} data-id="element-194">
                  See How It Works
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-4 text-sm text-gray-500" data-id="element-195">
                <div className="flex -space-x-2" data-id="element-196">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-bold" data-id="element-197">
                      {String.fromCharCode(64 + i)}
                    </div>)}
                </div>
                <p data-id="element-198">Trusted by 2,000+ teams</p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 50
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: 'easeOut'
          }} className="relative" data-id="element-199">
              <div className="relative z-10 bg-white rounded-2xl shadow-2xl border border-gray-200 p-2" data-id="element-200">
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80" alt="Dashboard Interface" className="rounded-xl w-full h-auto" data-id="element-201" />

                {/* Floating UI Elements */}
                <motion.div initial={{
                x: 20,
                opacity: 0
              }} animate={{
                x: 0,
                opacity: 1
              }} transition={{
                delay: 1,
                duration: 0.5
              }} className="absolute -right-6 top-20 bg-white p-4 rounded-lg shadow-xl border border-gray-100 max-w-xs" data-id="element-202">
                  <div className="flex items-center gap-3 mb-2" data-id="element-203">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center" data-id="element-204">
                      <CheckCircle className="w-5 h-5 text-green-600" data-id="element-205" />
                    </div>
                    <div data-id="element-206">
                      <p className="text-sm font-bold text-gray-900" data-id="element-207">
                        Cart Captured
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-208">Ready to send</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div initial={{
                x: -20,
                opacity: 0
              }} animate={{
                x: 0,
                opacity: 1
              }} transition={{
                delay: 1.2,
                duration: 0.5
              }} className="absolute -left-6 bottom-20 bg-white p-4 rounded-lg shadow-xl border border-gray-100 max-w-xs" data-id="element-209">
                  <div className="flex items-center gap-3" data-id="element-210">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center" data-id="element-211">
                      <ShoppingCart className="w-6 h-6 text-[#6366F1]" data-id="element-212" />
                    </div>
                    <div data-id="element-213">
                      <p className="text-sm font-bold text-gray-900" data-id="element-214">
                        Amazon Cart
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-215">3 items • $429.00</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Problem Statement */}
      <Section className="bg-white" data-id="element-216">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-3xl" data-id="element-217">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-218">
            Stop manually copying purchase details
          </h2>
          <p className="text-lg text-gray-600 mb-12" data-id="element-219">
            No more screenshots, spreadsheets, or email chains. Capture cart
            information from any website and send it where it needs to go.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-220">
            <div className="p-6 rounded-xl bg-gray-50" data-id="element-221">
              <ShoppingCart className="w-10 h-10 text-[#6366F1] mx-auto mb-4" data-id="element-222" />
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-223">
                Manual Entry
              </h3>
              <p className="text-gray-600" data-id="element-224">
                Copy-pasting item details into forms and emails.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50" data-id="element-225">
              <FileSpreadsheet className="w-10 h-10 text-[#6366F1] mx-auto mb-4" data-id="element-226" />
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-227">
                Lost Information
              </h3>
              <p className="text-gray-600" data-id="element-228">
                Missing SKUs, prices, and product details.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50" data-id="element-229">
              <Users className="w-10 h-10 text-[#6366F1] mx-auto mb-4" data-id="element-230" />
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-231">
                Slow Approvals
              </h3>
              <p className="text-gray-600" data-id="element-232">
                Waiting for finance to process incomplete requests.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* 3-Step Solution */}
      <Section className="bg-gradient-to-br from-[#EEF2FF] to-white" data-id="element-233">
        <div className="container mx-auto px-4 md:px-6" data-id="element-234">
          <div className="text-center mb-16" data-id="element-235">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-236">
              How it works
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-237">
              Three simple steps to streamline your purchasing process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-238">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gray-200 z-0" data-id="element-239"></div>

            {[{
            step: '01',
            title: 'Shop Anywhere',
            desc: 'Browse and add items to your cart on Amazon, Newegg, Shopify, or any online store.',
            icon: <ShoppingCart className="w-6 h-6 text-white" data-id="element-240" />
          }, {
            step: '02',
            title: 'Capture Cart Info',
            desc: 'Click the Cart Tracker extension to automatically capture all item details, prices, and SKUs.',
            icon: <Download className="w-6 h-6 text-white" data-id="element-241" />
          }, {
            step: '03',
            title: 'Send or Download',
            desc: 'Send directly to your procurement system or download as Excel to share with your finance team.',
            icon: <Send className="w-6 h-6 text-white" data-id="element-242" />
          }].map((item, i) => <Card key={i} className="relative z-10 p-8 text-center h-full" hoverEffect data-id="element-243">
                <div className="w-16 h-16 rounded-2xl bg-[#6366F1] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-900/20" data-id="element-244">
                  {item.icon}
                </div>
                <div className="text-sm font-bold text-[#EC4899] mb-2" data-id="element-245">
                  STEP {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-246">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-247">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </Section>

      {/* Benefits */}
      <Section className="bg-white" data-id="element-248">
        <div className="container mx-auto px-4 md:px-6" data-id="element-249">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-250">
            <div data-id="element-251">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-252">
                Built for speed. <br data-id="element-253" />
                <span className="text-[#6366F1]" data-id="element-254">Loved by teams.</span>
              </h2>
              <div className="space-y-6" data-id="element-255">
                {['One-click cart capture on any website', 'Automatic item details, SKUs, and pricing extraction', 'Export to Excel or send to procurement systems', 'Works with Amazon, Shopify, Newegg, and more', 'No manual data entry required'].map((benefit, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.1
              }} className="flex items-center gap-4" data-id="element-256">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-257">
                      <CheckCircle className="w-4 h-4 text-green-600" data-id="element-258" />
                    </div>
                    <span className="text-lg text-gray-700" data-id="element-259">{benefit}</span>
                  </motion.div>)}
              </div>
              <div className="mt-10" data-id="element-260">
                <Button variant="secondary" className="gap-2" data-id="element-261">
                  Explore All Features <ArrowRight className="w-4 h-4" data-id="element-262" />
                </Button>
              </div>
            </div>
            <div className="relative" data-id="element-263">
              <div className="absolute inset-0 bg-gradient-to-tr from-[#6366F1]/20 to-[#EC4899]/20 rounded-2xl transform rotate-3" data-id="element-264"></div>
              <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80" alt="Analytics Dashboard" className="relative rounded-2xl shadow-2xl border border-gray-100" data-id="element-265" />
            </div>
          </div>
        </div>
      </Section>

      {/* Dual CTA */}
      <Section className="bg-gradient-to-br from-[#EEF2FF] to-white" data-id="element-266">
        <div className="container mx-auto px-4 md:px-6" data-id="element-267">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-268">
            <Card className="p-10 flex flex-col items-start h-full border-t-4 border-t-[#6366F1]" data-id="element-269">
              <div className="w-12 h-12 bg-[#6366F1]/10 rounded-lg flex items-center justify-center mb-6" data-id="element-270">
                <Users className="w-6 h-6 text-[#6366F1]" data-id="element-271" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-272">
                For Procurement Teams
              </h3>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-273">
                Capture purchase requests with complete details. No more chasing
                employees for missing information.
              </p>
              <Button variant="primary" className="w-full sm:w-auto" data-id="element-274">
                Install Chrome Extension
              </Button>
            </Card>

            <Card className="p-10 flex flex-col items-start h-full border-t-4 border-t-[#EC4899]" data-id="element-275">
              <div className="w-12 h-12 bg-[#EC4899]/10 rounded-lg flex items-center justify-center mb-6" data-id="element-276">
                <BarChart3 className="w-6 h-6 text-[#EC4899]" data-id="element-277" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-278">
                For Finance Teams
              </h3>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-279">
                Receive structured purchase data in Excel format. Perfect for
                approval workflows and record keeping.
              </p>
              <Button variant="secondary" className="w-full sm:w-auto" onClick={() => window.open('https://calendly.com/alex-thruline/cart-tracker', '_blank')} data-id="element-280">
                See How It Works
              </Button>
            </Card>
          </div>
        </div>
      </Section>
    </div>;
}